import React from 'react';
import {
  VStack,
  Heading,
  Text,
  Button,
  useToast,
  Spinner,
  Center,
  SimpleGrid,
  Badge,
  Box,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { Match, LeagueId } from '../../types';
import {
  groupMatchesForDisplay,
  buildMundialCaptureSections,
  canUserPredictMatch,
  type MundialCaptureSection,
} from '../../services/matchdayService';
import { MatchPickCard } from './MatchPickCard';
import { useAuth } from '../../context/AuthContext';
import { quinielaService } from '../../services/quinielaService';
import { SportsCard } from '../ui/SportsCard';
import { useAppColors } from '../../hooks/useAppColors';

interface QuinielaFormProps {
  matches: Match[];
  league?: LeagueId;
  jornadaLabel?: string;
  allGroupMatches?: Match[];
  onSubmit?: () => void | Promise<void>;
  isLoading?: boolean;
  teamLogos?: { [teamName: string]: string };
  userPredictions: { match_id: string; predicted_score_a: number; predicted_score_b: number }[];
}

type DraftScore = { score_a?: number; score_b?: number };

export const QuinielaForm: React.FC<QuinielaFormProps> = ({
  matches,
  league = 'liga_mx',
  jornadaLabel,
  allGroupMatches,
  onSubmit,
  isLoading,
  teamLogos,
  userPredictions,
}) => {
  const [predictions, setPredictions] = React.useState<Record<string, DraftScore>>({});
  const { user } = useAuth();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { subtle, accent } = useAppColors();

  const capturedMatchIds = React.useMemo(
    () => new Set(userPredictions.map((p) => p.match_id)),
    [userPredictions]
  );

  const hasCompleteDraft = (draft?: DraftScore): draft is { score_a: number; score_b: number } =>
    draft?.score_a !== undefined && draft?.score_b !== undefined;

  const getPendingSaves = () =>
    matches.filter((match) => {
      if (capturedMatchIds.has(match.id)) return false;
      if (!canUserPredictMatch(user?.uid, match)) return false;
      return hasCompleteDraft(predictions[match.id]);
    });

  if (isLoading) {
    return (
      <Center p={12}>
        <Spinner size="xl" color="pitch.500" />
      </Center>
    );
  }

  if (!matches.length) {
    return (
      <SportsCard>
        <Center p={8}>
          <Text textAlign="center" color={subtle}>
            {jornadaLabel
              ? `No hay partidos abiertos para captura en ${jornadaLabel}.`
              : 'No hay partidos disponibles en este momento.'}
          </Text>
        </Center>
      </SportsCard>
    );
  }

  const handleScoreChange = (matchId: string, team: 'a' | 'b', value: number) => {
    setPredictions((prev) => ({
      ...prev,
      [matchId]: {
        score_a: team === 'a' ? value : prev[matchId]?.score_a,
        score_b: team === 'b' ? value : prev[matchId]?.score_b,
      },
    }));
  };

  const handleSubmit = async () => {
    if (!user) {
      toast({
        title: 'Error',
        description: 'Debes iniciar sesi?n para guardar tus predicciones',
        status: 'error',
        duration: 3000,
      });
      return;
    }
    if (isSubmitting) return;

    const toSave = getPendingSaves();
    if (toSave.length === 0) {
      toast({
        title: 'Sin pron?sticos nuevos',
        description: 'Captura al menos un partido pendiente antes de guardar',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    const closedMatches = toSave.filter((m) => !canUserPredictMatch(user.uid, m));
    if (closedMatches.length > 0) {
      toast({
        title: 'Partido cerrado',
        description: 'Uno o m?s partidos ya iniciaron y no se pueden pronosticar',
        status: 'error',
        duration: 4000,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const payload: Record<string, { score_a: number; score_b: number }> = {};
      const matchesById: Record<string, Match> = {};
      for (const match of toSave) {
        const draft = predictions[match.id];
        if (!hasCompleteDraft(draft)) continue;
        payload[match.id] = { score_a: draft.score_a, score_b: draft.score_b };
        matchesById[match.id] = match;
      }

      const saved = await quinielaService.submitPredictions(
        user.uid,
        payload,
        league,
        matchesById,
        capturedMatchIds
      );

      if (saved === 0) {
        toast({
          title: 'No se guard? nada',
          description: 'Los partidos seleccionados ya no est?n disponibles para captura',
          status: 'warning',
          duration: 3000,
        });
        return;
      }

      setPredictions((prev) => {
        const next = { ...prev };
        toSave.forEach((m) => delete next[m.id]);
        return next;
      });

      toast({
        title: 'Pron?sticos guardados',
        description:
          saved === 1
            ? 'Se guard? 1 pron?stico. Puedes capturar el resto cuando quieras.'
            : `Se guardaron ${saved} pron?sticos. Puedes capturar el resto cuando quieras.`,
        status: 'success',
        duration: 4000,
      });

      await onSubmit?.();
    } catch (error) {
      console.error('Error guardando predicciones:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron guardar tus predicciones',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const mundialSections: MundialCaptureSection[] =
    league === 'mundial_fifa' ? buildMundialCaptureSections(matches) : [];
  const useMundialSectionTabs = mundialSections.length > 1;
  const singleMundialSection = mundialSections.length === 1 ? mundialSections[0] : null;
  const displayGroups =
    league === 'mundial_fifa' && singleMundialSection?.kind === 'groups' && singleMundialSection.groups
      ? singleMundialSection.groups
      : league !== 'mundial_fifa'
        ? groupMatchesForDisplay(matches, league, allGroupMatches)
        : [];
  const pendingCount = getPendingSaves().length;
  const multipleJornadas =
    singleMundialSection?.kind === 'groups' &&
    (singleMundialSection.jornadaBlocks?.length ?? 0) > 1;
  const multipleKnockoutPhases =
    singleMundialSection?.kind === 'knockout' &&
    (singleMundialSection.knockoutBlocks?.length ?? 0) > 1;
  const multipleSubTabs = multipleJornadas || multipleKnockoutPhases;

  const renderMatchGroups = (
    groups: { key: string; label: string; matches: Match[] }[]
  ) =>
    groups.map(({ key, label, matches: groupMatches }) => (
      <Box key={key}>
        <Heading size="sm" mb={3} color={accent} letterSpacing="wide">
          {label.toUpperCase()}
        </Heading>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          {groupMatches.map((match) => {
            const userPrediction = userPredictions.find((p) => p.match_id === match.id);
            const hasResult = match.score_a !== null && match.score_b !== null;
            const isCaptured = !!userPrediction;
            const isOpen = canUserPredictMatch(user?.uid, match);
            const draft = predictions[match.id];

            return (
              <MatchPickCard
                key={match.id}
                match={match}
                teamLogos={teamLogos}
                scoreA={userPrediction?.predicted_score_a ?? draft?.score_a}
                scoreB={userPrediction?.predicted_score_b ?? draft?.score_b}
                onScoreChange={(team, v) => handleScoreChange(match.id, team, v)}
                disabled={hasResult || !isOpen}
                locked={isCaptured && !hasResult}
                closed={!isOpen && !hasResult && !isCaptured}
              />
            );
          })}
        </SimpleGrid>
      </Box>
    ));

  const renderKnockoutBlock = (
    block: { key?: string; phase: string; label: string; matches: Match[] }
  ) => (
    <Box key={block.phase}>
      {!multipleKnockoutPhases && (
        <Heading size="sm" mb={3} color={accent} letterSpacing="wide">
          {block.label.toUpperCase()}
        </Heading>
      )}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {block.matches.map((match) => {
          const userPrediction = userPredictions.find((p) => p.match_id === match.id);
          const hasResult = match.score_a !== null && match.score_b !== null;
          const isCaptured = !!userPrediction;
          const isOpen = canUserPredictMatch(user?.uid, match);
          const draft = predictions[match.id];

          return (
            <MatchPickCard
              key={match.id}
              match={match}
              teamLogos={teamLogos}
              scoreA={userPrediction?.predicted_score_a ?? draft?.score_a}
              scoreB={userPrediction?.predicted_score_b ?? draft?.score_b}
              onScoreChange={(team, v) => handleScoreChange(match.id, team, v)}
              disabled={hasResult || !isOpen}
              locked={isCaptured && !hasResult}
              closed={!isOpen && !hasResult && !isCaptured}
            />
          );
        })}
      </SimpleGrid>
    </Box>
  );

  const renderMundialSection = (section: MundialCaptureSection) => {
    if (section.kind === 'groups') {
      const blocks = section.jornadaBlocks ?? [];
      if (blocks.length > 1) {
        return (
          <Tabs variant="soft-rounded" colorScheme="pitch" size="sm" isLazy>
            <TabList flexWrap="wrap" gap={1} mb={4}>
              {blocks.map((block) => (
                <Tab key={block.jornada}>Jornada {block.jornada}</Tab>
              ))}
            </TabList>
            <TabPanels>
              {blocks.map((block) => (
                <TabPanel key={block.jornada} px={0} pt={2}>
                  {renderMatchGroups(block.groups)}
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        );
      }
      return renderMatchGroups(section.groups ?? blocks[0]?.groups ?? []);
    }

    const blocks = section.knockoutBlocks ?? [];
    if (blocks.length > 1) {
      return (
        <Tabs variant="soft-rounded" colorScheme="pitch" size="sm" isLazy>
          <TabList flexWrap="wrap" gap={1} mb={4}>
            {blocks.map((block) => (
              <Tab key={block.phase}>{block.label}</Tab>
            ))}
          </TabList>
          <TabPanels>
            {blocks.map((block) => (
              <TabPanel key={block.phase} px={0} pt={2}>
                {renderKnockoutBlock(block)}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      );
    }
    return blocks.map((block) => renderKnockoutBlock(block));
  };

  return (
    <VStack spacing={6} align="stretch">
      {jornadaLabel && (
        <SportsCard accent p={4}>
          <HStack justify="space-between" flexWrap="wrap" gap={2}>
            <Badge bg="gold.500" color="slate.900" fontSize="sm" px={2}>
              {useMundialSectionTabs
                ? 'Fases abiertas'
                : multipleSubTabs
                  ? 'Varias rondas abiertas'
                  : singleMundialSection?.kind === 'knockout'
                    ? 'Eliminatorias'
                    : 'Jornada activa'}
            </Badge>
            {!useMundialSectionTabs && !multipleSubTabs && (
              <Text fontWeight="bold" fontSize="md">
                {jornadaLabel}
              </Text>
            )}
          </HStack>
          <Text fontSize="sm" color={subtle} mt={2}>
            {useMundialSectionTabs
              ? 'Hay fase de grupos y eliminatorias abiertas. Elige la pesta?a correspondiente para capturar antes del inicio de cada partido.'
              : multipleSubTabs
                ? 'Hay varias jornadas o rondas abiertas. Usa las pesta?as para capturar tus pron?sticos antes de la hora de inicio.'
                : 'Captura los partidos que quieras antes de su hora de inicio. Los ya guardados no se pueden modificar.'}
          </Text>
        </SportsCard>
      )}

      {useMundialSectionTabs ? (
        <Tabs variant="enclosed" colorScheme="pitch" isLazy>
          <TabList flexWrap="wrap">
            {mundialSections.map((section) => (
              <Tab key={section.key}>{section.label}</Tab>
            ))}
          </TabList>
          <TabPanels>
            {mundialSections.map((section) => (
              <TabPanel key={section.key} px={0} pt={4}>
                {renderMundialSection(section)}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      ) : league === 'mundial_fifa' && singleMundialSection ? (
        renderMundialSection(singleMundialSection)
      ) : (
        renderMatchGroups(displayGroups)
      )}

      <Button
        size="lg"
        colorScheme="gold"
        onClick={handleSubmit}
        isLoading={isSubmitting}
        isDisabled={isSubmitting || pendingCount === 0}
        w="full"
      >
        {pendingCount > 0
          ? `Guardar pron?sticos (${pendingCount})`
          : 'Guardar pron?sticos'}
      </Button>
    </VStack>
  );
};
